import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useForm from "../../hooks/useForm";
import { useThunk } from "../../hooks/useThunk";
import AuthHeader from "../../components/Login/AuthHeader";
import AuthFooter from "../../components/Login/AuthFooter";
import { errorMessages } from "../../constants/frontendErrorMessages";
import showToast from "../../utils/toastNotifications";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import CustomButton from "../../components/Form/CustomButton";
import Input from "../../components/Form/Input";
import { setPassword, validateToken } from "../../services/thunks/remixerUserThunks";

const SetRemixerPassword = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [linkVerified, setLinkVerified] = useState(true);
  const [verifyError, setVerifyError] = useState(null);
  const [doVerifyPasswordForgot, isVerifying] = useThunk(validateToken);
  const [doChangePassword, isPasswordChanging] = useThunk(setPassword);
  const [searchParams, setSearchParam] = useSearchParams();

  const token = searchParams.get("token");
  const id = searchParams.get("id");

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
    clearError,
  } = useForm(
    {
      password: "",
      confirmPassword: "",
    },
    {
      password: true,
      confirmPassword: true,
    },
    {
      password: COMMON_FIELD_TYPES.PASSWORD_FIELD,
      confirmPassword: COMMON_FIELD_TYPES.CONFIRM_PASSWORD,
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      password: true,
      confirmPassword: true,
    });

    if (validateForm()) {
      const result = await doChangePassword({ token, id, data: formData });

      if (result.success) {
        navigate("/signin", { replace: true });
        showToast("success", result.response?.message);
      } else {
        showToast("error", result.error?.message);
      }
    }
  };

  useEffect(() => {
    if (!token || !id) {
      setLinkVerified(false);
      setVerifyError(errorMessages.FAILED_RESET_PASSWORD);
      return;
    }
    // check if user has valid link to change the password with a backend call
    doVerifyPasswordForgot({ id, token }).then((result) => {
      if (result?.success) {
        setLinkVerified(true);
      } else {
        setLinkVerified(false);
        setVerifyError(result?.error?.message);
      }
    });
  }, [id, token, doVerifyPasswordForgot]);

  const handleOkayButton = () => {
    navigate("/signin", { replace: true });
  };

  return (
    <div>
      {/* <<<=== spinner with loading dialog box while link is verifying ===>>> */}
      {isVerifying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="p-3 sm:p-4 md:p-5 bg-[#1A1924] rounded-lg shadow-lg w-80 sm:w-96">
            <h2 className="text-lg sm:text-xl font-bold text-[#FFFFFF] font-inter">
              Verifying Link...
            </h2>
            <div className="flex items-center justify-center mt-3 sm:mt-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 border-t-4 rounded-full loader border-blue-shades animate-spin"></div>
            </div>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base text-[#FFFFFF] font-inter">
              Please wait while we verify your password reset link.
            </p>
          </div>
        </div>
      )}

      {/* Invalid Link notify Dialog Box */}
      {!linkVerified && !isVerifying && (
        <div>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 font-inter">
            <div className="p-3 sm:p-4 md:p-5 bg-[#1A1924] rounded-lg shadow-lg w-80 sm:w-96">
              <h2 className="text-lg sm:text-xl font-bold text-[#FFFFFF]">Invalid Link</h2>
              <p className="mt-2 text-sm sm:text-base text-[#FFFFFF]">
                {verifyError}. Please request a new Link.
              </p>
              <div className="flex justify-end mt-3 sm:mt-4">
                <button
                  className="px-3 sm:px-4 py-2 text-sm sm:text-base rounded bg-primary text-[#FFFFFF] hover:bg-blue-shades"
                  onClick={handleOkayButton}
                >
                  Okay
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="relative flex items-center justify-center w-full min-h-screen overflow-hidden">
        {/* Main background with gradient */}
        <div
          className="absolute inset-0"
          style={{
            background:
              " linear-gradient(180deg, rgb(16, 16, 24) 0%, 64.9979%, rgb(21, 48, 66) 79.9154%, 89.1379%, rgb(24, 78, 117) 100%)",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
            filter: "blur(-20px)",
          }}
        />

        {/* Content container */}
        <div className="relative z-10 container flex justify-center items-center overflow-hidden px-2 sm:px-6 md:px-8">
          <div className="flex flex-col items-center w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl pt-4 sm:pt-6 md:pt-8 lg:pt-10 pb-3 sm:pb-4 md:pb-5">
            
            {/* Logo */}
            <AuthHeader />
            
            {/* Form Container */}
            <div className="w-full px-2 sm:px-6 md:px-8 lg:px-10 py-6 sm:py-8 md:py-10 rounded-lg shadow-lg bg-[#1A1924] font-public-sans font-bold text-sm sm:text-base md:text-lg leading-none tracking-normal text-center">
              
              {/* Title */}
              <h1 className="mb-6 sm:mb-8 md:mb-10 text-xl md:text-[1.625rem] font-bold text-[#B3B3B3]">
                Set password
              </h1>

              <form onSubmit={handleSubmit}>
                {/* Form Fields Container */}
                <div className="space-y-2 sm:space-y-3 w-full max-w-[18rem] sm:max-w-[20rem] md:max-w-[22rem] mx-auto">
                  <div className="relative">
                    <Input
                      label="New Password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      error={touched.password && errors.password}
                      onBlur={() => handleBlur("password", formData.password)}
                      onFocus={() => clearError("password")}
                      onChange={(e) =>
                        handleInputChange("password", e.target.value)
                      }
                      isRequired={true}
                      className="h-9 sm:h-10 md:h-11  w-full"
                      placeholder="Enter new password"
                      errorContainerStyle="h-5 mt-1"
                      icon={
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 flex items-center pr-3"
                        >
                          {showPassword ? (
                            <svg
                              width="20"
                              height="20"
                              className="sm:w-6 sm:h-6"
                              viewBox="0 0 27 27"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M13.5 16.875C15.364 16.875 16.875 15.364 16.875 13.5C16.875 11.636 15.364 10.125 13.5 10.125C11.636 10.125 10.125 11.636 10.125 13.5C10.125 15.364 11.636 16.875 13.5 16.875Z"
                                fill="white"
                              />
                              <path
                                d="M20.8206 7.44504C18.515 5.86406 16.0476 5.0625 13.4873 5.0625C11.1834 5.0625 8.93742 5.74805 6.8117 7.09172C4.66805 8.44963 2.56658 10.916 0.84375 13.5C2.23699 15.8203 4.14281 18.206 6.12773 19.5739C8.4048 21.1423 10.8807 21.9375 13.4873 21.9375C16.0713 21.9375 18.5419 21.1428 20.8332 19.5755C22.8503 18.1934 24.7699 15.8108 26.1562 13.5C24.7651 11.2097 22.8393 8.82984 20.8206 7.44504ZM13.5 18.5625C12.4987 18.5625 11.5199 18.2656 10.6874 17.7093C9.8549 17.153 9.20603 16.3624 8.82286 15.4373C8.43969 14.5123 8.33944 13.4944 8.53477 12.5124C8.73011 11.5303 9.21227 10.6283 9.92027 9.92027C10.6283 9.21227 11.5303 8.73011 12.5124 8.53477C13.4944 8.33944 14.5123 8.43969 15.4373 8.82286C16.3624 9.20603 17.153 9.8549 17.7093 10.6874C18.2656 11.5199 18.5625 12.4987 18.5625 13.5C18.561 14.8422 18.0271 16.129 17.078 17.078C16.129 18.0271 14.8422 18.561 13.5 18.5625Z"
                                fill="white"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="20"
                              height="20"
                              className="sm:w-6 sm:h-6"
                              viewBox="0 0 27 27"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M13.3087 10.125L16.875 13.68V13.5C16.875 12.6049 16.5194 11.7464 15.8865 11.1135C15.2535 10.4806 14.3951 10.125 13.5 10.125H13.3087ZM8.47125 11.025L10.215 12.7687C10.1587 13.005 10.125 13.2412 10.125 13.5C10.125 14.3951 10.4806 15.2535 11.1135 15.8865C11.7464 16.5194 12.6049 16.875 13.5 16.875C13.7475 16.875 13.995 16.8412 14.2312 16.785L15.975 18.5287C15.2212 18.9 14.3887 19.125 13.5 19.125C12.0082 19.125 10.5774 18.5324 9.52252 17.4775C8.46763 16.4226 7.875 14.9918 7.875 13.5C7.875 12.6112 8.1 11.7787 8.47125 11.025ZM2.25 4.80375L4.815 7.36875L5.32125 7.875C3.465 9.3375 2.0025 11.25 1.125 13.5C3.07125 18.4387 7.875 21.9375 13.5 21.9375C15.2437 21.9375 16.9087 21.6 18.4275 20.9925L18.9112 21.465L22.1962 24.75L23.625 23.3212L3.67875 3.375M13.5 7.875C14.9918 7.875 16.4226 8.46763 17.4775 9.52252C18.5324 10.5774 19.125 12.0082 19.125 13.5C19.125 14.22 18.9787 14.9175 18.72 15.5475L22.0162 18.8437C23.7037 17.4375 25.0537 15.5925 25.875 13.5C23.9287 8.56125 19.125 5.0625 13.5 5.0625C11.925 5.0625 10.4175 5.34375 9 5.85L11.4412 8.26875C12.0825 8.02125 12.7687 7.875 13.5 7.875Z"
                                fill="#C8C8C8"
                              />
                            </svg>
                          )}
                        </button>
                      }
                    />
                  </div>
                  
                  <div className="relative">
                    <Input
                      label="Re-Enter Password"
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      error={touched.confirmPassword && errors.confirmPassword}
                      onBlur={() =>
                        handleBlur("confirmPassword", formData.confirmPassword)
                      }
                      onFocus={() => clearError("confirmPassword")}
                      isRequired={true}
                      onChange={(e) =>
                        handleInputChange("confirmPassword", e.target.value)
                      }
                      className="h-9 sm:h-10 md:h-11 w-full"
                      placeholder="Confirm new password"
                      errorContainerStyle="h-5 mt-1"
                      icon={
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 flex items-center pr-3"
                        >
                          {showConfirmPassword ? (
                            <svg
                              width="20"
                              height="20"
                              className="sm:w-6 sm:h-6"
                              viewBox="0 0 27 27"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M13.5 16.875C15.364 16.875 16.875 15.364 16.875 13.5C16.875 11.636 15.364 10.125 13.5 10.125C11.636 10.125 10.125 11.636 10.125 13.5C10.125 15.364 11.636 16.875 13.5 16.875Z"
                                fill="white"
                              />
                              <path
                                d="M20.8206 7.44504C18.515 5.86406 16.0476 5.0625 13.4873 5.0625C11.1834 5.0625 8.93742 5.74805 6.8117 7.09172C4.66805 8.44963 2.56658 10.916 0.84375 13.5C2.23699 15.8203 4.14281 18.206 6.12773 19.5739C8.4048 21.1423 10.8807 21.9375 13.4873 21.9375C16.0713 21.9375 18.5419 21.1428 20.8332 19.5755C22.8503 18.1934 24.7699 15.8108 26.1562 13.5C24.7651 11.2097 22.8393 8.82984 20.8206 7.44504ZM13.5 18.5625C12.4987 18.5625 11.5199 18.2656 10.6874 17.7093C9.8549 17.153 9.20603 16.3624 8.82286 15.4373C8.43969 14.5123 8.33944 13.4944 8.53477 12.5124C8.73011 11.5303 9.21227 10.6283 9.92027 9.92027C10.6283 9.21227 11.5303 8.73011 12.5124 8.53477C13.4944 8.33944 14.5123 8.43969 15.4373 8.82286C16.3624 9.20603 17.153 9.8549 17.7093 10.6874C18.2656 11.5199 18.5625 12.4987 18.5625 13.5C18.561 14.8422 18.0271 16.129 17.078 17.078C16.129 18.0271 14.8422 18.561 13.5 18.5625Z"
                                fill="white"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="20"
                              height="20"
                              className="sm:w-6 sm:h-6"
                              viewBox="0 0 27 27"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M13.3087 10.125L16.875 13.68V13.5C16.875 12.6049 16.5194 11.7464 15.8865 11.1135C15.2535 10.4806 14.3951 10.125 13.5 10.125H13.3087ZM8.47125 11.025L10.215 12.7687C10.1587 13.005 10.125 13.2412 10.125 13.5C10.125 14.3951 10.4806 15.2535 11.1135 15.8865C11.7464 16.5194 12.6049 16.875 13.5 16.875C13.7475 16.875 13.995 16.8412 14.2312 16.785L15.975 18.5287C15.2212 18.9 14.3887 19.125 13.5 19.125C12.0082 19.125 10.5774 18.5324 9.52252 17.4775C8.46763 16.4226 7.875 14.9918 7.875 13.5C7.875 12.6112 8.1 11.7787 8.47125 11.025ZM2.25 4.80375L4.815 7.36875L5.32125 7.875C3.465 9.3375 2.0025 11.25 1.125 13.5C3.07125 18.4387 7.875 21.9375 13.5 21.9375C15.2437 21.9375 16.9087 21.6 18.4275 20.9925L18.9112 21.465L22.1962 24.75L23.625 23.3212L3.67875 3.375M13.5 7.875C14.9918 7.875 16.4226 8.46763 17.4775 9.52252C18.5324 10.5774 19.125 12.0082 19.125 13.5C19.125 14.22 18.9787 14.9175 18.72 15.5475L22.0162 18.8437C23.7037 17.4375 25.0537 15.5925 25.875 13.5C23.9287 8.56125 19.125 5.0625 13.5 5.0625C11.925 5.0625 10.4175 5.34375 9 5.85L11.4412 8.26875C12.0825 8.02125 12.7687 7.875 13.5 7.875Z"
                                fill="#C8C8C8"
                              />
                            </svg>
                          )}
                        </button>
                      }
                    />
                  </div>
                  
                  {/* Set Password Button */}
                  <CustomButton
                    type="submit"
                    buttonText="Set Password"
                    onClick={handleSubmit}
                    loading={isPasswordChanging}
                    className="mt-3 sm:mt-4 w-full h-9 sm:h-10 md:h-11 lg:h-[3.875rem] text-base sm:text-lg md:text-xl lg:text-[22px]"
                  />
                  
                  {/* Back Link */}
                  <Link
                    to="/signin"
                    className="font-normal text-[12px] sm:text-base md:text-lg text-[#FFFFFF] font-public-sans underline mt-3 sm:mt-4 md:mt-5 block"
                  >
                    Back
                  </Link>
                </div>
              </form>
            </div>
           
            {/* Footer */}
            <AuthFooter />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetRemixerPassword;